import {
  DraftSection,
  Requirement,
  RequiredForm,
  StaffMember,
  TeamMemberWorkload,
  ProposalSectionGroup,
  RequirementCategory,
} from "../types";
import { saveDraftSection } from "./draftsService";
import { bulkUpdateRequirements } from "./requirementsService";

export type { TeamMemberWorkload, ProposalSectionGroup };

export interface TeamWorkloadSummary {
  members: TeamMemberWorkload[];
  unassigned: {
    sectionsCount: number;
    requirementsCount: number;
    formsCount: number;
    totalCount: number;
  };
  totals: {
    sectionsCount: number;
    requirementsCount: number;
    formsCount: number;
    totalDraftedWords: number;
    totalTargetWords: number;
  };
}

/**
 * Calculates workload metrics and allocation statistics for all team members.
 */
export function calculateTeamWorkloads(
  staffList: StaffMember[],
  sections: DraftSection[],
  requirements: Requirement[],
  forms: RequiredForm[],
): TeamWorkloadSummary {
  // Build a distinct set of all candidate members (authorized staff + any assignee on items)
  const memberMap = new Map<
    string,
    { displayName: string; email: string; department?: string; role?: string }
  >();

  staffList.forEach((s) => {
    if (s.email) {
      memberMap.set(s.email.toLowerCase(), {
        displayName: s.displayName,
        email: s.email,
        department: s.department || undefined,
        role: s.role || undefined,
      });
    }
  });

  const registerAssignee = (displayName?: string, email?: string) => {
    if (!email) return;
    const lower = email.toLowerCase();
    if (!memberMap.has(lower)) {
      memberMap.set(lower, {
        displayName: displayName || email.split("@")[0] || "Team Member",
        email,
      });
    }
  };

  sections.forEach((s) => {
    registerAssignee(s.assignedTo, s.assignedToEmail);
    registerAssignee(s.reviewerName, s.reviewerEmail);
  });
  requirements.forEach((r) => registerAssignee(r.assignedTo, r.assignedToEmail));
  forms.forEach((f) => registerAssignee(f.assignedTo, f.assignedToEmail));

  // Compute stats per team member
  const members: TeamMemberWorkload[] = Array.from(memberMap.values()).map((info) => {
    const emailLower = info.email.toLowerCase();

    const ownedSections = sections.filter((s) => s.assignedToEmail?.toLowerCase() === emailLower);
    const reviewingSections = sections.filter((s) => s.reviewerEmail?.toLowerCase() === emailLower);
    const assignedReqs = requirements.filter(
      (r) => r.assignedToEmail?.toLowerCase() === emailLower,
    );
    const assignedForms = forms.filter((f) => f.assignedToEmail?.toLowerCase() === emailLower);

    const draftedWordCount = ownedSections.reduce((acc, s) => acc + (s.wordCount || 0), 0);
    const targetWordCount = ownedSections.reduce((acc, s) => acc + (s.targetWordCount || 0), 0);

    // Status breakdown
    let pending = 0;
    let inProgress = 0;
    let review = 0;
    let approved = 0;

    ownedSections.forEach((s) => {
      if (s.status === "approved") approved++;
      else if (s.status === "review") review++;
      else if (s.wordCount > 0) inProgress++;
      else pending++;
    });

    assignedReqs.forEach((r) => {
      if (r.confirmed || r.status === "confirmed") approved++;
      else if (r.status === "answered_library") review++;
      else if (r.status === "partial") inProgress++;
      else pending++;
    });

    assignedForms.forEach((f) => {
      if (f.status === "signed" || f.status === "waived") approved++;
      else if (f.status === "attached") review++;
      else if (f.status === "in_progress") inProgress++;
      else pending++;
    });

    const completedItemsCount = approved;
    const totalItemsCount = ownedSections.length + assignedReqs.length + assignedForms.length;

    let loadLevel: TeamMemberWorkload["loadLevel"];
    if (totalItemsCount <= 3) {
      loadLevel = "light";
    } else if (totalItemsCount <= 7) {
      loadLevel = "balanced";
    } else if (totalItemsCount <= 12) {
      loadLevel = "heavy";
    } else {
      loadLevel = "overloaded";
    }

    return {
      email: info.email,
      displayName: info.displayName,
      department: info.department,
      role: info.role,
      sectionsOwnedCount: ownedSections.length,
      sectionsReviewingCount: reviewingSections.length,
      requirementsCount: assignedReqs.length,
      formsCount: assignedForms.length,
      draftedWordCount,
      targetWordCount,
      completedItemsCount,
      totalItemsCount,
      statusBreakdown: {
        pending,
        inProgress,
        review,
        approved,
      },
      loadLevel,
    };
  });

  // Sort members by total items descending, then by name
  members.sort(
    (a, b) => b.totalItemsCount - a.totalItemsCount || a.displayName.localeCompare(b.displayName),
  );

  // Calculate unassigned items
  const unassignedSections = sections.filter((s) => !s.assignedToEmail && !s.assignedTo).length;
  const unassignedReqs = requirements.filter((r) => !r.assignedToEmail && !r.assignedTo).length;
  const unassignedForms = forms.filter((f) => !f.assignedToEmail && !f.assignedTo).length;

  const totalDraftedWords = sections.reduce((acc, s) => acc + (s.wordCount || 0), 0);
  const totalTargetWords = sections.reduce((acc, s) => acc + (s.targetWordCount || 0), 0);

  return {
    members,
    unassigned: {
      sectionsCount: unassignedSections,
      requirementsCount: unassignedReqs,
      formsCount: unassignedForms,
      totalCount: unassignedSections + unassignedReqs + unassignedForms,
    },
    totals: {
      sectionsCount: sections.length,
      requirementsCount: requirements.length,
      formsCount: forms.length,
      totalDraftedWords,
      totalTargetWords,
    },
  };
}

/**
 * Harmonizes explicit DraftSections with Requirements to build a unified proposal outline.
 */
export function buildProposalSectionGroups(
  sections: DraftSection[],
  requirements: Requirement[],
): ProposalSectionGroup[] {
  const result: ProposalSectionGroup[] = [];
  const claimedReqIds = new Set<string>();

  // 1. Process explicit DraftSections first
  sections.forEach((sec) => {
    const matchedReqs: Requirement[] = [];

    // Match by direct requirementId
    if (sec.requirementId) {
      const direct = requirements.find((r) => r.id === sec.requirementId);
      if (direct) {
        matchedReqs.push(direct);
        claimedReqIds.add(direct.id);
      }
    }

    // Match other requirements by sectionRef if present
    if (sec.sectionRef) {
      requirements.forEach((r) => {
        if (
          !claimedReqIds.has(r.id) &&
          r.sectionRef &&
          r.sectionRef.trim().toLowerCase() === sec.sectionRef?.trim().toLowerCase()
        ) {
          matchedReqs.push(r);
          claimedReqIds.add(r.id);
        }
      });
    }

    const targetWordCount =
      sec.targetWordCount || (matchedReqs.length > 0 ? matchedReqs.length * 350 : 500);
    const actualWordCount = sec.wordCount || 0;

    let groupStatus: ProposalSectionGroup["status"] = sec.status || "draft";
    if (!sec.assignedTo && !sec.assignedToEmail) {
      groupStatus = "unassigned";
    }

    let primaryCat: RequirementCategory = "general";
    if (sec.category) {
      primaryCat = sec.category;
    } else if (matchedReqs.length > 0 && matchedReqs[0]) {
      primaryCat = matchedReqs[0].category;
    }

    const defaultRef = sec.title.includes(" - ")
      ? (sec.title.split(" - ")[0] || "SEC").trim()
      : "SEC";

    result.push({
      id: sec.id,
      sectionRef: sec.sectionRef || defaultRef,
      title: sec.title,
      category: primaryCat,
      draftSection: sec,
      requirements: matchedReqs,
      assignedTo: sec.assignedTo,
      assignedToEmail: sec.assignedToEmail,
      reviewerName: sec.reviewerName,
      reviewerEmail: sec.reviewerEmail,
      targetWordCount,
      actualWordCount,
      dueDate: sec.dueDate,
      status: groupStatus,
      notes: sec.notes,
    });
  });

  // 2. Group remaining unclaimed requirements by sectionRef or category
  const remainingReqs = requirements.filter((r) => !claimedReqIds.has(r.id));
  const virtualGroupMap = new Map<
    string,
    { sectionRef: string; category: RequirementCategory; title: string; reqs: Requirement[] }
  >();

  remainingReqs.forEach((r) => {
    const groupKey = r.sectionRef?.trim() || `cat-${r.category}`;
    if (!virtualGroupMap.has(groupKey)) {
      const title = r.sectionRef
        ? r.sectionRef
        : `${r.category.charAt(0).toUpperCase() + r.category.slice(1)} Response`;
      virtualGroupMap.set(groupKey, {
        sectionRef: r.sectionRef || r.category.toUpperCase().slice(0, 4),
        category: r.category,
        title,
        reqs: [],
      });
    }
    virtualGroupMap.get(groupKey)?.reqs.push(r);
  });

  virtualGroupMap.forEach((vGroup, key) => {
    // Determine representative assignee from underlying requirements
    const firstAssigned = vGroup.reqs.find((r) => r.assignedTo || r.assignedToEmail);
    const targetWordCount = vGroup.reqs.length * 350;

    result.push({
      id: `virtual-${key}`,
      sectionRef: vGroup.sectionRef,
      title: vGroup.title,
      category: vGroup.category,
      requirements: vGroup.reqs,
      assignedTo: firstAssigned?.assignedTo,
      assignedToEmail: firstAssigned?.assignedToEmail,
      targetWordCount,
      actualWordCount: 0,
      status: firstAssigned ? "draft" : "unassigned",
    });
  });

  return result;
}

/**
 * Auto-creates persistent DraftSection records in Firestore for any requirement groups that lack them.
 */
export async function autoInitializeDraftSections(
  opportunityId: string,
  requirements: Requirement[],
  existingSections: DraftSection[],
  user?: { displayName?: string | null; email?: string | null },
): Promise<number> {
  const groups = buildProposalSectionGroups(existingSections, requirements);
  let initializedCount = 0;

  for (const group of groups) {
    // Only initialize virtual groups that don't yet have a backing DraftSection
    if (!group.draftSection && group.requirements.length > 0) {
      const primaryReq = group.requirements[0];
      if (!primaryReq) continue;
      await saveDraftSection(
        opportunityId,
        {
          requirementId: primaryReq.id,
          title: group.title,
          sectionRef: group.sectionRef,
          category: group.category,
          assignedTo: group.assignedTo,
          assignedToEmail: group.assignedToEmail,
          reviewerName: group.reviewerName,
          reviewerEmail: group.reviewerEmail,
          targetWordCount: group.targetWordCount,
          dueDate: group.dueDate,
          notes: group.notes,
          content: "",
          status: "draft",
          humanEdited: false,
        },
        user,
      );
      initializedCount++;
    }
  }

  return initializedCount;
}

/**
 * Updates a DraftSection's assignment and allocation fields.
 */
export async function updateSectionAllocation(
  opportunityId: string,
  section: DraftSection,
  updates: Partial<DraftSection>,
  user?: { displayName?: string | null; email?: string | null },
): Promise<string> {
  return await saveDraftSection(
    opportunityId,
    {
      id: section.id,
      requirementId: section.requirementId,
      title: updates.title !== undefined ? updates.title : section.title,
      content: section.content,
      status: updates.status !== undefined ? updates.status : section.status,
      sectionRef: updates.sectionRef !== undefined ? updates.sectionRef : section.sectionRef,
      category: updates.category !== undefined ? updates.category : section.category,
      assignedTo: updates.assignedTo !== undefined ? updates.assignedTo : section.assignedTo,
      assignedToEmail:
        updates.assignedToEmail !== undefined ? updates.assignedToEmail : section.assignedToEmail,
      reviewerName:
        updates.reviewerName !== undefined ? updates.reviewerName : section.reviewerName,
      reviewerEmail:
        updates.reviewerEmail !== undefined ? updates.reviewerEmail : section.reviewerEmail,
      targetWordCount:
        updates.targetWordCount !== undefined ? updates.targetWordCount : section.targetWordCount,
      dueDate: updates.dueDate !== undefined ? updates.dueDate : section.dueDate,
      notes: updates.notes !== undefined ? updates.notes : section.notes,
    },
    user,
  );
}

/**
 * Cascades a section lead / drafter assignment to all underlying requirements in that section.
 */
export async function cascadeSectionAssignee(
  opportunityId: string,
  requirementIds: string[],
  staff: { displayName: string; email: string } | null,
): Promise<void> {
  if (!requirementIds.length) return;
  await bulkUpdateRequirements(opportunityId, requirementIds, {
    assignedTo: staff?.displayName || "",
    assignedToEmail: staff?.email || "",
  });
}
